import React, { useState } from 'react';
import { useGlobals } from '@bwoty-web/ui-kit/hooks';
import { ICON_UI_SMART_FILTER, ICON_UI_WARNING } from '@bwoty-web/ui-kit/icons';
import { Button, ExpandableLink, Icon, TextArea } from '@bwoty-web/ui-kit';

import { useInitialState } from '../../hooks/useInitialState';
import { usePax } from '../../hooks/usePax';
import { useDuration } from '../../hooks/useDuration';
import { useDepartures } from '../../hooks/useDepartures';
import { useDepartureDate } from '../../hooks/useDepartureDate';
import { useHotelsContext } from '../../context/Hotels';
import { useLocationContext } from '../../context/Location';
import { ANALYTICS_DATA_TYPES } from '../../hooks/useAnalytics';
import { logSmartFilterEvents } from '../../utils/smartSearchAnalytics';

import './smartFilter.scss';

const SmartFilter = () => {
  const { baseUrl } = useGlobals();
  const { translationDictionary, geographicalDictionary } = useInitialState();
  const { refetchHotels } = useHotelsContext();
  const location = useLocationContext();
  const { searchParams } = location;
  const { pax } = usePax(searchParams);
  const { selectedDepartures } = useDepartures(searchParams);
  const { duration } = useDuration(searchParams);
  const { departureDate } = useDepartureDate(searchParams);

  const [isLinkExpanded, setIsLinkExpanded] = useState(false);
  const [textAreaValue, setTextAreaValue] = useState(searchParams.smartQuery || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const expandableLinkText = isLinkExpanded ? 'Visa färre exempel' : 'Visa fler exempel';

  const btnOnClickHandler = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const myHeaders = new Headers();
      myHeaders.append('X-Session-Id', crypto.randomUUID());
      myHeaders.append('Content-Type', 'application/json');

      const [adult, child] = pax.split('_').map((num) => parseInt(num, 10));

      const raw = JSON.stringify({
        language: 'sv',
        departure: selectedDepartures,
        ...(departureDate && { depDate: departureDate }),
        duration,
        pax: {
          adult,
          child,
        },
        query: textAreaValue,
      });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw,
        redirect: 'follow',
      };

      const fetchUrl = `${baseUrl}/suggestFilters`;
      const response = await fetch(fetchUrl, requestOptions);
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      const body = await response.json();
      if (!body?.success) {
        throw new Error(body?.reason || 'Failed to fetch suggested filters');
      }

      const suggestedFilters = {};
      const filterKeys = Array.isArray(body?.filters) ? body.filters : [];
      filterKeys.forEach((key) => {
        if (key) {
          suggestedFilters[key] = true;
        }
      });

      const suggestedRangeFilters = {};
      const rangeFilters = Array.isArray(body?.rangeFilters) ? body.rangeFilters : [];
      rangeFilters.forEach((rangeFilter) => {
        const key = rangeFilter?.key;
        if (!key) return;

        suggestedRangeFilters[key] = rangeFilter.limit;
      });

      refetchHotels({
        newSearchParams: {
          smartQuery: textAreaValue.replace(/[^\w+ ,åäöÅÄÖ]/g, ''),
          filters: suggestedFilters,
          ...suggestedRangeFilters,
        },
        analyticsData: {
          type: ANALYTICS_DATA_TYPES.SMART_FILTER,
          params: {
            log: (filterEvent) => logSmartFilterEvents({ body, translationDictionary, geographicalDictionary, filterEvent }),
          },
        },
        clearAllFilters: true,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onFeedbackHandler = (e) => {
    e.preventDefault();
    const url = new URL(window.location.href);
    url.searchParams.set('showfeedback', 'true');
    window.location.href = url;
  };

  return (
    <div className="hf-smart-filter-container">
      <span className="hf-smart-filter-news">Beta</span>
      <div className="hf-smart-filter-heading-container">
        <Icon size="s" path={ICON_UI_SMART_FILTER} />
        <span className="hf-smart-filter-heading">Smart filter</span>
      </div>
      <div className="hf-smart-filter-input-container">
        <span className="hf-smart-filter-input-label">Vad letar du efter?</span>
        <TextArea
          className="hf-smart-filter-textarea"
          data-cs-mask
          onChange={(e) => setTextAreaValue(e.target.value)}
          placeholder="Prova något i stil med “Hotell med all inclusive nära stranden”"
          value={textAreaValue}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              btnOnClickHandler();
            }
          }}
        />
        {isLinkExpanded && (
          <div className="hf-smart-filter-example-container">
            <p className="hf-smart-filter-example-text">Här är exempel på vad du kan skriva: </p>
            <ul className="hf-smart-filter-example-list">
              <li className="hf-smart-filter-example-list-item">
                Vi vill åka till barnvänligt hotell med pool under 20 000 kronor
              </li>
              <li className="hf-smart-filter-example-list-item">
                Hotell för vuxna nära havet med frukost
              </li>
            </ul>
          </div>
        )}
        <ExpandableLink
          isOpen={isLinkExpanded}
          onClick={() => {
            setIsLinkExpanded(!isLinkExpanded);
          }}
        >
          {expandableLinkText}
        </ExpandableLink>
      </div>
      {submitError && (
        <div className="hf-smart-filter-error-container">
          <Icon size="xs" path={ICON_UI_WARNING} alt="" />
          <p className="hf-smart-filter-error-heading">Inga matchande filter</p>
          <p
            className="hf-smart-filter-error-text"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: submitError }}
          />
        </div>
      )}
      <Button
        onClick={btnOnClickHandler}
        variant="tertiary"
        className="hf-smart-filter-apply-btn"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Söker...' : 'Hitta Hotell'}
      </Button>
      <button type="button" onClick={onFeedbackHandler} className="hf-smart-filter-feedback-button">
        Inte nöjd med resultatet? Lämna feedback
      </button>
    </div>
  );
};

export default SmartFilter;
